import { Box, Stack, Typography } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SystemLinearMeter } from "../../../../components/UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid, type SystemGridColDef } from "../../../../components/UIToolkit/Tables/SystemDataGrid";
import type { PbjBuildingAuditSummary } from "../../../../utils/models";
import type { SortingState } from "../../../../utils/multiColumnSorting";
import { dataTableSx } from "../../PredictionTab/colors";
import { boldNameColumn } from "../../pbjGridCells";
import { pbjSortOptions } from "../../pbjGridSorting";
import type { AuditDestination, OpenAuditDestination } from "../auditSubTabs";
import type { SummaryFormatters } from "./useSummaryFormatters";

export type BuildingSummaryRow = PbjBuildingAuditSummary & { buildingName: string; hoursToReview: number; clean: number | null };

type CountField = "missingPunchCount" | "longShiftCount" | "shortShiftCount" | "terminatedCount" | "unmappedJobCount" | "breaksRemovedCount";

/** Each count column, the header it shows and the list a click opens. Counts match the Audit tab badges. */
const COUNT_COLUMNS: readonly { field: CountField; headerKey: string; destination: AuditDestination }[] = [
	{ field: "missingPunchCount", headerKey: "pbj_model.audit.tabs.missing_punches", destination: "missing_punches" },
	{ field: "longShiftCount", headerKey: "pbj_model.audit.tabs.long_shifts", destination: "long_shifts" },
	{ field: "shortShiftCount", headerKey: "pbj_model.audit.tabs.short_shifts", destination: "short_shifts" },
	{ field: "terminatedCount", headerKey: "pbj_model.audit.tabs.terminated", destination: "terminated" },
	{ field: "unmappedJobCount", headerKey: "pbj_model.network.summary.cols.unmapped_jobs", destination: "submission" },
	{ field: "breaksRemovedCount", headerKey: "pbj_model.audit.tabs.breaks_removed", destination: "breaks_removed" },
];

const MOST_TO_REVIEW_FIRST: SortingState<BuildingSummaryRow>[] = [{ sortColumns: ["hoursToReview"], sortOrder: "desc" }];
const PERCENT = 100;
/** The Clean meter's floor, so a narrow column still shows a bar beside its percentage. */
const CLEAN_METER_MIN_WIDTH_PX = 48;

type Props = {
	rows: BuildingSummaryRow[];
	loading: boolean;
	error: boolean;
	formatters: SummaryFormatters;
	onOpen: OpenAuditDestination;
};

/** One row per building: its totals and every Audit count, each count opening that list for the building. */
export const BuildingsSummaryTable: FC<Props> = ({ rows, loading, error, formatters: { formatHours, formatCount, formatPercent }, onOpen }) => {
	const { t } = useTranslation();

	const columns = useMemo<SystemGridColDef<BuildingSummaryRow>[]>(
		() => [
			boldNameColumn<BuildingSummaryRow>("buildingName", t("pbj_model.network.building"), { minWidth: 180 }),
			{
				field: "totalHours",
				headerName: t("pbj_model.network.summary.cols.total_hours"),
				type: "number",
				minWidth: 110,
				valueFormatter: (value: number) => formatHours(value),
			},
			{
				field: "hoursToReview",
				headerName: t("pbj_model.network.summary.cols.hours_to_review"),
				type: "number",
				minWidth: 130,
				valueFormatter: (value: number) => formatHours(value),
			},
			{
				field: "clean",
				headerName: t("pbj_model.network.summary.cols.clean"),
				minWidth: 150,
				valueFormatter: (value: number | null) => (value === null ? "—" : formatPercent(value)),
				renderCell: ({ row }: GridRenderCellParams<BuildingSummaryRow>) =>
					row.clean === null ? (
						"—"
					) : (
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", height: "100%" }}>
							<Box sx={{ flex: 1, minWidth: CLEAN_METER_MIN_WIDTH_PX }}>
								<SystemLinearMeter value={row.clean * PERCENT} max={PERCENT} ariaLabel={formatPercent(row.clean)} />
							</Box>
							<Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
								{formatPercent(row.clean)}
							</Typography>
						</Stack>
					),
			},
			...COUNT_COLUMNS.map(
				({ field, headerKey, destination }): SystemGridColDef<BuildingSummaryRow> => ({
					field,
					headerName: t(headerKey),
					type: "number",
					minWidth: 120,
					renderCell: ({ row }: GridRenderCellParams<BuildingSummaryRow>) => (
						<SystemButton
							buttonVariant="borderless"
							size="small"
							noFullWidthMobile
							disabled={row[field] === 0}
							onClick={() => onOpen(destination, row.departmentId)}
							aria-label={t("pbj_model.network.summary.open_count", { count: row[field], list: t(headerKey), building: row.buildingName })}
							data-testid={`pbj-network-summary-count-${field}-${row.departmentId}`}
						>
							{row[field] === 0 ? "—" : formatCount(row[field])}
						</SystemButton>
					),
				}),
			),
		],
		[t, formatHours, formatCount, formatPercent, onOpen],
	);

	const sortingOptions = useMemo(
		() =>
			pbjSortOptions<BuildingSummaryRow>([
				{ field: "buildingName", label: t("pbj_model.network.building") },
				{ field: "totalHours", label: t("pbj_model.network.summary.cols.total_hours") },
				{ field: "hoursToReview", label: t("pbj_model.network.summary.cols.hours_to_review") },
				{ field: "clean", label: t("pbj_model.network.summary.cols.clean") },
				...COUNT_COLUMNS.map(({ field, headerKey }) => ({ field, label: t(headerKey) })),
			]),
		[t],
	);

	return (
		<SystemDataGrid<BuildingSummaryRow>
			rows={rows}
			columns={columns}
			getRowId={(row) => row.departmentId}
			sortingOptions={sortingOptions}
			defaultSortState={MOST_TO_REVIEW_FIRST}
			showSortSummary
			searchableColumns={["buildingName"]}
			searchPlaceholder={t("pbj_model.network.summary.search")}
			exportOptions={{ fileName: t("pbj_model.network.summary.export_file_name") }}
			loading={loading}
			error={error}
			errorMessage={t("pbj_model.network.summary.failed")}
			defaultPageSize={50}
			sx={(theme) => dataTableSx(theme)}
			dataTestId="pbj-network-summary-buildings"
		/>
	);
};
